import { useEffect, useMemo, useState } from 'react';
import { Badge, Banner, Button, ButtonLink, Card, EmptyState, FINDING_STATUS_TONE, Icon, Input, SectionTitle, SEVERITY_TONE, Skeleton, SURVEY_STATUS_TONE } from '../../components';
import { downloadBlob } from '../../components/download';
import { withCurrentDue } from '../../lib/sync/pack';
import { useI18n } from '../../lib/i18n';
import { Link, useNavigate, useParams, useSearchParams } from '../../lib/router';
import type { PackSite, SiteDetail, SiteListItem } from '../../lib/api/types';
import { normalise, siteMatches } from '../../lib/offline/types';
import { useQuery } from '../../lib/query';
import { PageHeader } from '../../app/AppShell';
import { useDebounced, useDrafts, useOnline, usePackSite, usePackSites, useSiteSearch } from '../../app/hooks';
import { useMe } from '../../app/me';
import { useServices } from '../../app/services';
import { useToast } from '../../components/Toast';
import { SurveyIdLabel } from '../capture/CapturePages';
import { DueBadge, SiteCard } from './SiteCard';
import { startSurvey } from './start-survey';

/** GET /sites items and GET /sites/:id (masterdata siteDto, camelCase) → the device's site model. */
function toPackSite(s: SiteListItem | SiteDetail): PackSite {
  return {
    id: s.id,
    code: s.code,
    name: s.name,
    customerId: s.customerId,
    customerName: s.customerName ?? undefined,
    customerCode: s.customerCode ?? undefined,
    orgPath: s.orgPath,
    regionCode: s.regionCode,
    countryCode: s.countryCode,
    branchCode: s.branchCode,
    serviceLine: s.serviceLine,
    address: s.address,
    lat: s.lat,
    lng: s.lng,
    status: s.status,
    enrolments:
      'enrolments' in s
        ? s.enrolments.map((e) => ({ surveyType: e.surveyType, nextDueDate: e.nextDueDate, daysToFollowUp: e.daysToFollowUp, lastSurveyDate: e.lastSurveyDate }))
        : undefined,
  };
}

export function SitesPage() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const debounced = useDebounced(q, 250);
  const online = useOnline();
  const pack = usePackSites();
  const local = useMemo(
    () =>
      (pack.data ?? [])
        .filter((s) => siteMatches(s, debounced))
        .sort((a, b) => (a.enrolments?.[0]?.daysToFollowUp ?? 9999) - (b.enrolments?.[0]?.daysToFollowUp ?? 9999) || a.name.localeCompare(b.name)),
    [pack.data, debounced],
  );
  const remote = useSiteSearch(debounced, online);
  const remoteOnly = (remote.data?.items ?? []).filter((r) => !local.some((l) => l.id === r.id)).map(toPackSite);

  useEffect(() => {
    setParams(debounced ? { q: debounced } : {}, { replace: true });
  }, [debounced]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <PageHeader title={t('ui.nav.sites')} subtitle={t('ui.sites.subtitle')} />
      <div className="relative mb-4">
        <Icon name="search" className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-muted" />
        <Input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ui.sites.searchPlaceholder')} className="ps-11" aria-label={t('ui.common.search')} />
      </div>
      {pack.loading ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {local.map((s) => (
            <SiteCard key={s.id} site={s} />
          ))}
          {remoteOnly.length > 0 && <SectionTitle>{t('ui.sites.moreOnline')}</SectionTitle>}
          {remoteOnly.map((s) => (
            <SiteCard key={s.id} site={s} onlineOnly />
          ))}
          {local.length === 0 && remoteOnly.length === 0 && (
            <EmptyState
              icon="mapPin"
              title={debounced ? t('ui.sites.noMatch') : t('ui.sites.emptyPack')}
              description={!online ? t('ui.sites.offlineSearch') : normalise(debounced).length === 1 ? t('ui.sites.typeMore') : undefined}
            />
          )}
        </div>
      )}
    </>
  );
}

/**
 * Site profile. Offline it shows what the device holds (the pack: site,
 * due dates, carried-forward findings, this device's drafts). Online it
 * always reads GET /sites/:id as well: fresh due dates, the site's recent
 * surveys, reports, open findings and documents (A7) — what desk users come
 * here for.
 */
export function SitePage() {
  const { id } = useParams<{ id: string }>();
  const { t, fmt } = useI18n();
  const services = useServices();
  const { api, storage, drafts } = services;
  const { me, can, canAny } = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  const local = usePackSite(id);
  const online = useOnline();
  const capture = can('survey.create');
  const remote = useQuery({
    queryKey: ['sites', id],
    queryFn: () => api.get<SiteDetail>(`/sites/${encodeURIComponent(id)}`),
    enabled: online,
  });
  const [downloading, setDownloading] = useState<string | null>(null);
  const [carried, setCarried] = useState<Awaited<ReturnType<typeof storage.findingsForSite>>>([]);
  const allDrafts = useDrafts();
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    storage.findingsForSite(id).then(setCarried, () => {});
  }, [id, storage]);

  const detail = remote.data;
  // The device's copy (it carries the template ids for offline capture), with the server's fresher due dates when online.
  const site: PackSite | undefined = local.data
    ? detail
      ? withCurrentDue({ ...local.data, enrolments: toPackSite(detail).enrolments })
      : local.data
    : detail
      ? withCurrentDue(toPackSite(detail))
      : undefined;
  const siteDrafts = (allDrafts.data ?? []).filter((d) => d.siteId === id && d.reporterUserId === me.user.id);
  const enrolments = site?.enrolments?.length ? site.enrolments : capture ? [{ surveyType: 'SSS', nextDueDate: null, daysToFollowUp: null }] : [];

  if (local.loading || (remote.isLoading && !site)) return <Skeleton className="h-40 w-full" />;
  if (!site) return <EmptyState icon="mapPin" title={t('ui.common.notFound')} />;

  const download = async (path: string, name: string) => {
    setDownloading(path);
    try {
      downloadBlob(await api.blob(path), name);
    } catch {
      toast.show(t('ui.sites.downloadFailed'), 'danger');
    } finally {
      setDownloading(null);
    }
  };

  const start = async (surveyType: string) => {
    setStarting(true);
    try {
      const r = await startSurvey({ api, storage, drafts }, site, surveyType, me.user.id);
      if (!r.ok) {
        toast.show(t('ui.sites.noTemplate'), 'danger');
        return;
      }
      if (r.resumed) toast.show(t('ui.sites.resumed'));
      navigate(`/capture/${r.draft.id}`);
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <PageHeader
        title={site.name}
        subtitle={[site.customerName, site.code].filter(Boolean).join(' · ')}
        back="/sites"
        actions={
          online &&
          canAny('masterdata.edit', 'survey.schedule') && (
            <ButtonLink to={`/admin/sites/${site.id}`} variant="secondary" icon="pen">
              {t('ui.sites.edit')}
            </ButtonLink>
          )
        }
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <Card className="p-4">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <Info label={t('ui.sites.customer')} value={site.customerName} />
              <Info label={t('ui.sites.serviceLine')} value={site.serviceLine} />
              <Info label={t('ui.sites.branch')} value={site.branchCode} />
              <Info label={t('ui.sites.address')} value={site.address} />
            </dl>
            {site.lat != null && site.lng != null && (
              <a className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand" href={`https://www.google.com/maps?q=${site.lat},${site.lng}`} target="_blank" rel="noreferrer">
                <Icon name="mapPin" size={16} /> {t('ui.sites.openMap')}
              </a>
            )}
          </Card>

          {carried.length > 0 && (
            <div>
              <SectionTitle>{t('ui.sites.openFindings')}</SectionTitle>
              <Card className="divide-y divide-border">
                {carried.map((f) => (
                  <div key={f.id} className="flex items-center gap-3 p-3">
                    <Badge tone={SEVERITY_TONE[f.severity] ?? 'neutral'}>{t(`severity.${f.severity}`)}</Badge>
                    <span className="min-w-0 flex-1 truncate font-mono text-sm">{f.humanId}</span>
                    <Badge tone={FINDING_STATUS_TONE[f.status] ?? 'neutral'}>{t(`findingStatus.${f.status}`)}</Badge>
                  </div>
                ))}
              </Card>
            </div>
          )}

          {siteDrafts.length > 0 && (
            <div>
              <SectionTitle>{t('ui.sites.yourSurveys')}</SectionTitle>
              <div className="flex flex-col gap-2">
                {siteDrafts.map((d) => (
                  <Card key={d.id} interactive className="flex items-center gap-3 p-3" onClick={() => navigate(`/capture/${d.id}`)} role="link">
                    <Icon name="clipboard" className="text-brand" />
                    <span className="min-w-0 flex-1 text-sm">
                      <SurveyIdLabel draft={d} />
                    </span>
                    <Badge>{t(`surveyStatus.${d.status}`)}</Badge>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {detail && detail.access?.surveys !== false && (
            <div>
              <SectionTitle>{t('ui.sites.recentSurveys')}</SectionTitle>
              {detail.recentSurveys.length === 0 ? (
                <Card>
                  <EmptyState icon="clipboard" title={t('ui.sites.noSurveys')} />
                </Card>
              ) : (
                <Card className="divide-y divide-border">
                  {detail.recentSurveys.map((v) => (
                    <Link key={v.id} to={`/surveys/${v.id}`} className="flex flex-wrap items-center gap-3 p-3 hover:bg-surface-2">
                      <Icon name="clipboard" className="text-brand" />
                      <span className="min-w-0 flex-1 text-sm">
                        <span className="font-mono font-semibold">{v.humanId ?? '—'}</span>
                        <span className="block truncate text-muted">
                          {[v.surveyType, v.surveyDate ? fmt.date(v.surveyDate) : null, v.reporterName].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      <Badge tone={SURVEY_STATUS_TONE[v.status] ?? 'neutral'}>{t(`surveyStatus.${v.status}`)}</Badge>
                      <Icon name="chevronRight" className="text-muted rtl:rotate-180" />
                    </Link>
                  ))}
                </Card>
              )}
            </div>
          )}

          {detail && detail.reports.length > 0 && (
            <div>
              <SectionTitle>{t('ui.sites.reports')}</SectionTitle>
              <Card className="divide-y divide-border">
                {detail.reports.map((r) => (
                  <Link key={r.id} to={`/surveys/${r.surveyId}`} className="flex flex-wrap items-center gap-3 p-3 hover:bg-surface-2">
                    <Icon name="fileText" className="text-brand" />
                    <span className="min-w-0 flex-1 text-sm">
                      <span className="font-semibold">{t('ui.sites.reportLine', { id: r.surveyHumanId ?? '—', version: r.version, lang: r.language.toUpperCase() })}</span>
                      <span className="block text-muted">{r.sentAt ? t('ui.sites.reportSent', { date: fmt.date(r.sentAt.slice(0, 10)) }) : t('ui.sites.reportNotSent')}</span>
                    </span>
                    <Icon name="chevronRight" className="text-muted rtl:rotate-180" />
                  </Link>
                ))}
              </Card>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {enrolments.map((e) => (
            <Card key={e.surveyType} className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <span className="font-bold">{e.surveyType}</span>
                <DueBadge days={e.daysToFollowUp} />
              </div>
              {e.nextDueDate && <p className="text-sm text-muted">{t('ui.sites.nextDue', { date: fmt.date(e.nextDueDate) })}</p>}
              {can('survey.create') && (
                <Button size="lg" block icon="plus" loading={starting} onClick={() => start(e.surveyType)}>
                  {t('ui.sites.startSurvey')}
                </Button>
              )}
            </Card>
          ))}
          {detail && detail.access?.findings !== false && (
            <Card className="flex flex-col gap-2 p-4">
              <span className="font-bold">{t('ui.sites.openFindingsTitle')}</span>
              {detail.openFindings.total === 0 ? (
                <p className="text-sm text-muted">{t('ui.sites.noOpenFindings')}</p>
              ) : (
                <>
                  <p className="text-sm">
                    {t('ui.sites.openFindingsCount', { count: detail.openFindings.total })}
                    {detail.openFindings.overdue > 0 && <span className="font-semibold text-danger"> · {t('ui.sites.overdueFindings', { count: detail.openFindings.overdue })}</span>}
                  </p>
                  {can('finding.view') && (
                    <Link to={`/findings?siteId=${encodeURIComponent(site.id)}`} className="text-sm font-semibold text-brand underline">
                      {t('ui.sites.viewFindings')}
                    </Link>
                  )}
                </>
              )}
            </Card>
          )}
          {detail && detail.documents.length > 0 && (
            <Card className="flex flex-col gap-2 p-4">
              <span className="font-bold">{t('ui.sites.documents')}</span>
              {detail.documents.map((doc) => (
                <Button
                  key={doc.assetId}
                  variant="secondary"
                  icon="download"
                  loading={downloading === `/assets/${doc.assetId}`}
                  onClick={() => download(`/assets/${encodeURIComponent(doc.assetId)}`, `${site.code}-${doc.surveyType}-${t('ui.sites.historicalFile')}.pdf`)}
                >
                  {t('ui.sites.historicalReport', { type: doc.surveyType })}
                </Button>
              ))}
            </Card>
          )}
          {/* Only reporters care that a site is not on their phone; desk users never carry sites. */}
          {!local.data && capture && <Banner tone="info">{t('ui.sites.notInPack')}</Banner>}
        </div>
      </div>
    </>
  );
}

function Info({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-muted">{label}</dt>
      <dd className="font-medium">{value || '—'}</dd>
    </div>
  );
}
